import { useEffect, useState } from 'react';
import {
  CalendarDays,
  Check,
  Eye,
  Info,
  Loader2,
  Pencil,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendario, CalendarioAcesso, Usuario } from '@/types';
import { calendarioService } from '@/services/calendarioApi';
import { cn } from '@/lib/utils';

const PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calendario?: Calendario | null;
  usuarios: Pick<Usuario, 'id' | 'nome' | 'email'>[];
  onSaved: () => void;
  /** Quando informado (superadmin), envia empresa_id na criação */
  empresaId?: string;
}

export function CalendarioModal({ open, onOpenChange, calendario, usuarios, onSaved, empresaId }: Props) {
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [nome, setNome] = useState('');
  const [cor, setCor] = useState('#3B82F6');
  const [tipo, setTipo] = useState<'padrao' | 'entregas'>('padrao');
  const [descricao, setDescricao] = useState('');
  const [acessos, setAcessos] = useState<CalendarioAcesso[]>([]);
  const [novoUsuarioId, setNovoUsuarioId] = useState('');
  const [novoPodeEditar, setNovoPodeEditar] = useState(false);
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (calendario) {
      setNome(calendario.nome || '');
      setCor(calendario.cor || '#3B82F6');
      setTipo(calendario.tipo || 'padrao');
      setDescricao(calendario.descricao || '');
      setAcessos(calendario.acessos || []);
    } else {
      setNome('');
      setCor('#3B82F6');
      setTipo('padrao');
      setDescricao('');
      setAcessos([]);
    }
    setNovoUsuarioId('');
    setNovoPodeEditar(false);
  }, [open, calendario]);

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error('Informe o nome do calendário');
      return;
    }
    setLoading(true);
    try {
      if (calendario) {
        const res = await calendarioService.update(calendario.id, { nome: nome.trim(), cor, tipo, descricao: descricao || undefined });
        if (!res.success) throw new Error(res.error);
        toast.success('Calendário atualizado!');
      } else {
        const res = await calendarioService.create({
          nome: nome.trim(),
          cor,
          tipo,
          descricao: descricao || undefined,
          ...(empresaId ? { empresa_id: empresaId } : {}),
        });
        if (!res.success) throw new Error(res.error);
        toast.success('Calendário criado!');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar calendário');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!calendario) return;
    setLoading(true);
    try {
      const res = await calendarioService.remove(calendario.id);
      if (!res.success) throw new Error(res.error);
      toast.success('Calendário excluído!');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir calendário');
    } finally {
      setLoading(false);
      setDeleteOpen(false);
    }
  };

  const handleAddUser = async () => {
    if (!novoUsuarioId || !calendario) return;
    setAddingUser(true);
    try {
      const res = await calendarioService.addAcesso(calendario.id, {
        usuario_id: novoUsuarioId,
        pode_editar: novoPodeEditar,
      });
      if (!res.success) throw new Error(res.error);
      setAcessos(prev => {
        const exists = prev.find(a => a.usuario_id === novoUsuarioId);
        if (exists) {
          return prev.map(a => a.usuario_id === novoUsuarioId ? { ...a, pode_editar: novoPodeEditar } : a);
        }
        return [...prev, res.data];
      });
      setNovoUsuarioId('');
      setNovoPodeEditar(false);
      toast.success('Usuário adicionado!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao adicionar usuário');
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (acesso: CalendarioAcesso) => {
    if (!calendario) return;
    try {
      const res = await calendarioService.removeAcesso(calendario.id, acesso.usuario_id);
      if (!res.success) throw new Error(res.error);
      setAcessos(prev => prev.filter(a => a.usuario_id !== acesso.usuario_id));
      toast.success('Acesso removido!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao remover acesso');
    }
  };

  const handleTogglePodeEditar = async (acesso: CalendarioAcesso) => {
    if (!calendario) return;
    try {
      const res = await calendarioService.addAcesso(calendario.id, {
        usuario_id: acesso.usuario_id,
        pode_editar: !acesso.pode_editar,
      });
      if (!res.success) throw new Error(res.error);
      setAcessos(prev =>
        prev.map(a => a.usuario_id === acesso.usuario_id ? { ...a, pode_editar: !acesso.pode_editar } : a),
      );
    } catch (err) {
      toast.error('Erro ao alterar permissão');
    }
  };

  const usuariosDisponiveis = usuarios.filter(
    u => !acessos.find(a => a.usuario_id === u.id),
  );

  const modoTitulo = calendario ? 'Editar calendário' : 'Novo calendário';
  const previewNome = nome.trim() || 'Novo calendário';
  const previewDescricao = descricao.trim() || 'Adicione uma descrição para orientar a equipe sobre quando usar este calendário.';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="overflow-hidden border-0 p-0 shadow-2xl sm:max-w-4xl">
          <DialogHeader className="border-b px-6 py-5">
            <div className="flex items-start gap-4">
              <div
                className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border shadow-sm"
                style={{ backgroundColor: `${cor}18`, color: cor, borderColor: `${cor}45` }}
              >
                <CalendarDays className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-xl">{modoTitulo}</DialogTitle>
                <DialogDescription className="mt-1 max-w-2xl text-sm leading-6">
                  Organize a agenda com um nome claro, uma cor fácil de identificar e, se quiser, compartilhe o calendário com pessoas específicas.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid md:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-6 px-6 py-5">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Marketing, Comercial ou Diretoria"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  autoFocus
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground">
                  Escolha um nome curto e fácil de reconhecer na agenda.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de calendário</Label>
                <Select value={tipo} onValueChange={(v) => setTipo(v as 'padrao' | 'entregas')}>
                  <SelectTrigger id="tipo" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="padrao">Padrão</SelectItem>
                    <SelectItem value="entregas">Agendamento de entregas</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Calendários de entregas incluem campos de fornecedor, peso, volume e NFe nos eventos.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label>Cor</Label>
                  <Badge variant="outline" className="rounded-full border-transparent bg-muted/70 text-[11px] text-muted-foreground">
                    leitura rápida
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={cn(
                        'relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2',
                        cor === c ? 'ring-2 ring-offset-2' : 'border-white/70'
                      )}
                      style={{
                        backgroundColor: c,
                        boxShadow: cor === c ? `0 0 0 2px ${c}` : undefined,
                      }}
                      onClick={() => setCor(c)}
                      aria-label={`Cor ${c}`}
                    >
                      {cor === c && <Check className="h-4 w-4 text-white drop-shadow-sm" />}
                    </button>
                  ))}
                  <Input
                    type="color"
                    value={cor}
                    onChange={e => setCor(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded-xl border bg-background p-1"
                    title="Cor personalizada"
                  />
                  <div className="rounded-xl border bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                    {cor.toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  placeholder="Ex: calendário oficial do time para reuniões, entregas e marcos importantes."
                  rows={4}
                  value={descricao}
                  onChange={e => setDescricao(e.target.value)}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Use esse espaço para explicar o propósito do calendário.
                </p>
              </div>

              {calendario && (
                <Separator />
              )}

              {calendario && (
                <div className="space-y-4">
                  <div className="rounded-2xl border bg-muted/20 p-4">
                    <div className="flex items-start gap-3">
                      <Users className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium">Acessos ao calendário</div>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                          Convide pessoas da equipe e escolha se elas podem só visualizar ou também editar.
                        </p>
                      </div>
                    </div>
                  </div>

                  {acessos.length > 0 ? (
                    <div className="space-y-2.5">
                      {acessos.map(acesso => (
                        <div
                          key={acesso.usuario_id}
                          className="flex flex-col gap-3 rounded-2xl border bg-background px-4 py-3 text-sm md:flex-row md:items-center md:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {acesso.usuario?.nome || acesso.usuario_id}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {acesso.usuario?.email || 'Sem e-mail disponível'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 self-start md:self-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-full px-3 text-xs"
                              onClick={() => handleTogglePodeEditar(acesso)}
                              title="Alternar permissão"
                            >
                              {acesso.pode_editar ? (
                                <>
                                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                  Pode editar
                                </>
                              ) : (
                                <>
                                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                                  Só visualizar
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleRemoveUser(acesso)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                      Nenhum usuário com acesso adicional ainda.
                    </div>
                  )}

                  {usuariosDisponiveis.length > 0 && (
                    <div className="rounded-2xl border bg-muted/15 p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm font-medium">Adicionar pessoa</div>
                      </div>
                      <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex-1 space-y-1.5">
                          <Label className="text-xs">Usuário</Label>
                        <Select value={novoUsuarioId} onValueChange={setNovoUsuarioId}>
                            <SelectTrigger className="h-10 text-sm">
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {usuariosDisponiveis.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                        <div className="flex h-10 items-center gap-2 rounded-xl border bg-background px-3">
                          <Switch
                            id="novo-pode-editar"
                            checked={novoPodeEditar}
                            onCheckedChange={setNovoPodeEditar}
                          />
                          <Label htmlFor="novo-pode-editar" className="cursor-pointer text-xs whitespace-nowrap">
                            Permitir edição
                          </Label>
                        </div>
                        <Button
                          size="sm"
                          className="h-10 rounded-xl px-4"
                          onClick={handleAddUser}
                          disabled={!novoUsuarioId || addingUser}
                        >
                          {addingUser ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <UserPlus className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-2">Adicionar</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t bg-muted/15 px-6 py-5 md:border-l md:border-t-0">
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Prévia
                  </div>
                  <div className="mt-3 rounded-3xl border bg-background p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-3">
                      <div
                        className="h-4 w-4 rounded-full shadow-sm"
                        style={{ backgroundColor: cor }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{previewNome}</div>
                        <div className="text-xs text-muted-foreground">
                          {calendario ? 'Calendário existente' : 'Será criado ao salvar'}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Na agenda</span>
                        <Badge variant="outline" className="rounded-full">
                          {calendario ? 'editando' : 'novo'}
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2" style={{ backgroundColor: `${cor}14` }}>
                        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cor }} />
                        <span className="text-sm font-medium">{previewNome}</span>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-muted-foreground">
                        {previewDescricao}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-dashed bg-background/70 p-4">
                  <div className="flex items-start gap-2">
                    <Info className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Boas práticas</div>
                      <p className="text-xs leading-5 text-muted-foreground">
                        Padronize cores por área e compartilhe só com quem realmente acompanha essa agenda.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 border-t px-6 py-4">
            {calendario && (
              <Button
                variant="ghost"
                size="sm"
                className="mr-auto text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir calendário?</AlertDialogTitle>
            <AlertDialogDescription>
              O calendário <strong>{calendario?.nome}</strong> e todos os seus eventos serão excluídos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
